import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNotifier } from "react-headless-notifier";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  NotifierNotification,
  UnsavedChangesPrompt,
  Upload,
} from "@mwi/ui";

import { RootState } from "store";
import { setTheme } from "slices/practice";
import { useUpdatePracticeThemeMutation, useUploadFileMutation } from "api";
import useTitle from "hooks/useTitle";

type Input = {
  logo: { id: number | null; url: string | null };
};

const LogoPage = (): JSX.Element => {
  useTitle("Logo");

  const dispatch = useDispatch();
  const { notify } = useNotifier();

  const { practice, theme } = useSelector((state: RootState) => ({
    practice: state.practice,
    theme: state.practice.theme,
  }));

  const [uploadFile] = useUploadFileMutation();

  const {
    control,
    getValues,
    setValue,
    resetField,
    formState: { isDirty },
  } = useForm<Input>({
    defaultValues: {
      logo: {
        id: theme?.logoFileId,
        url: theme?.logoUrl,
      },
    },
  });

  const [
    updateTheme,
    {
      data: updatedTheme,
      isLoading: updatingTheme,
      isError: errorUpdatingTheme,
      isSuccess: didUpdateTheme,
    },
  ] = useUpdatePracticeThemeMutation();

  const handleSave = () => {
    if (practice?.id) {
      updateTheme({
        practiceId: practice.id,
        body: {
          logo_file_id: getValues("logo.id") ?? null,
        },
      });
    }
  };

  const handleDelete = () => {
    setValue("logo.id", null);
    setValue("logo.url", null);
  };

  useEffect(() => {
    if (didUpdateTheme) {
      // Reset fields being marked as dirty
      resetField("logo", {
        defaultValue: {
          id: getValues("logo.id"),
          url: getValues("logo.url"),
        },
      });

      notify(
        <NotifierNotification
          title="Theme Updated"
          message="Practice logo has been updated!"
        />,
      );
    }
  }, [didUpdateTheme]);

  useEffect(() => {
    if (updatedTheme) {
      dispatch(setTheme(updatedTheme));
    }
  }, [updatedTheme]);

  useEffect(() => {
    if (errorUpdatingTheme) {
      notify(
        <NotifierNotification
          type="error"
          title="Unable to update logo"
          message="We're unable to update your practice logo, please try again later."
        />,
      );
    }
  }, [errorUpdatingTheme]);

  return (
    <UnsavedChangesPrompt isDirty={isDirty}>
      <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6">
        <div className="col-span-full mb-14 text-lg flex flex-col gap-y-[2px]">
          <p className="font-semibold">Upload your company&apos;s logo</p>
          <p>This will be visible to all pet owners who access the platform</p>
        </div>

        <div className="relative col-start-1 col-end-6 mb-32">
          <Upload
            droppable
            name="logo"
            control={control}
            type="practice_logos"
            uploadFile={uploadFile}
            containerClassName="h-[195px]"
            defaultUrl={theme?.logoUrl}
            deleteFile={handleDelete}
            text="Drag & drop picture or click to browse"
            description="File must be JPG or PNG and photo less than 2 MB in size"
          />
        </div>

        <div className="col-span-full max-w-[182px]">
          <Button onClick={handleSave} loading={updatingTheme} block>
            Save changes
          </Button>
        </div>
      </div>
    </UnsavedChangesPrompt>
  );
};

export default LogoPage;
